import React, { useContext, useMemo, useState } from 'react';
import z from 'zod';
import {
  Body, ContactForm, ErrorMessage, Heading,
} from '@stereoagency/ui';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import Turnstile from 'react-turnstile';
import { LinesContext } from '@/components/LinesContext';
import { parseHtml } from '@/utils';
import {
  buildFieldRows,
  buildFormSchema,
  normalizeFields,
  toBoolean,
  toGenericFormType,
  RawCustomField,
} from '@/schemas/custom-form';

type Props = {
  data?: {
    label?: string
    title?: string
    description?: string
    form_id?: string
    recipient_key?: string
    fields?: RawCustomField[]
    consent_label?: string
    consent_required?: boolean | string
    submit_label?: string
    success_title?: string
    success_message?: string
    full_width?: boolean | string
    title_level?: string
  } | null
};

const titleLevels = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] as const;

type TitleLevel = typeof titleLevels[number];

function toTitleLevel(value?: string): TitleLevel {
  const level = value?.trim().toLowerCase();
  return titleLevels.includes(level as TitleLevel) ? (level as TitleLevel) : 'h2';
}

const turnstileEnabled = process.env.NEXT_PUBLIC_DISABLE_TURNSTILE !== 'true';

export default function NvisoCustomContactForm({ data }: Props) {
  const { enableLines } = useContext(LinesContext);
  const { t } = useTranslation();
  const router = useRouter();
  const [success, setSuccess] = useState<boolean | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const fields = useMemo(() => normalizeFields(data?.fields), [data?.fields]);
  const consentRequired = toBoolean(data?.consent_required, true);
  const consentLabel: string = data?.consent_label?.trim()
    || t('contact-form:consent.label')
    || '';

  const schema = useMemo(() => buildFormSchema(fields, {
    consentRequired,
    requireTurnstile: turnstileEnabled,
    messages: {
      required: t('contact-form:validation.required'),
      email: t('contact-form:validation.email'),
      number: t('contact-form:validation.number'),
      consent: t('contact-form:validation.consent'),
    },
  }), [fields, consentRequired, t]);

  const formId = data?.form_id?.trim() || 'custom-contact-form';

  const formConfig = useMemo(() => {
    const rows: any[][] = buildFieldRows(fields).map((row) => row.map((field) => ({
      name: field.name,
      type: toGenericFormType(field.type),
      label: field.label,
      placeholder: field.placeholder,
      helpText: field.help,
      required: field.required,
      options: field.type === 'select'
        ? [{ label: field.placeholder ?? '--', value: '' }, ...field.options]
        : field.options,
    })));

    rows.push([{
      name: '$consent',
      type: 'checkbox',
      label: parseHtml(consentLabel),
    } as any]);

    rows.push([{
      name: '$website',
      type: 'custom',
      label: 'Leave this field empty',
      render: (props: any) => (
        <input
          {...props}
          type="text"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          className="hidden"
        />
      ),
    } as any]);

    if (turnstileEnabled) {
      rows.push([{
        name: '$turnstile_key',
        type: 'custom',
        label: t('contact-form:fields.turnstile.label'),
        render: (props: any, errors: any) => (
          <div>
            <Turnstile
              sitekey={process.env.TURNSTILE_SITE_KEY ?? ''}
              onVerify={(value: string) => {
                props.onBlur({ target: { name: '$turnstile_key' } });
                props.onChange({ target: { name: '$turnstile_key', value } });
              }}
            />
            {!!errors?.$turnstile_key?.message && (
              <ErrorMessage className="mt-3">{errors.$turnstile_key.message}</ErrorMessage>
            )}
          </div>
        ),
      } as any]);
    }

    return rows;
  }, [fields, consentLabel, consentRequired, t]);

  const handleSubmit = async (values: z.infer<typeof schema>) => {
    try {
      setSubmitting(true);
      const response = await fetch('/api/form-submit', {
        method: 'POST',
        body: JSON.stringify({
          formId,
          formTitle: data?.title ?? formId,
          recipientKey: data?.recipient_key || 'default',
          pageUrl: typeof window !== 'undefined' ? window.location.href : undefined,
          pageTitle: typeof document !== 'undefined' ? document.title : undefined,
          locale: router.locale,
          consent: {
            text: consentLabel.replace(/<[^>]*>/g, '').trim(),
            accepted: values.$consent === true,
            required: consentRequired,
          },
          fields: fields.map((field) => ({
            name: field.name,
            label: field.label,
            type: field.type,
            value: values[field.name] ?? (field.type === 'checkbox' ? false : ''),
          })),
          $turnstile_key: values.$turnstile_key ?? 'disabled',
          $website: values.$website ?? '',
          debug: process.env.NODE_ENV === 'development',
        }),
        headers: { 'content-type': 'application/json' },
      }).then((r) => r.json()) as { success: boolean };
      setSuccess(response.success);
    } catch (err) {
      setSuccess(false);
    } finally {
      setSubmitting(false);
    }
  };

  if (!fields.length) return null;

  return (
    <ContactForm
      enableLines={enableLines}
      fullWidth={toBoolean(data?.full_width, false)}
      label={data?.label}
      title={data?.title}
      titleAs={toTitleLevel(data?.title_level)}
      description={data?.description ? parseHtml(data.description) : undefined}
      submitContent={data?.submit_label?.trim() || t('contact-form:submit')}
      isSubmitting={submitting}
      formConfig={{
        id: formId,
        schema,
        config: formConfig,
      }}
      errorContent={success === false ? t('contact-form:error.generic') : null}
      successContent={success ? (
        <div>
          <Heading variant="h3">
            {data?.success_title?.trim() || t('contact-form:success.title')}
          </Heading>
          <Body className="mt-3">
            {data?.success_message?.trim()
              ? parseHtml(data.success_message)
              : t('contact-form:success.description')}
          </Body>
        </div>
      ) : null}
      onSubmit={handleSubmit}
    />
  );
}
